import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/ui/Logo';
import toast from 'react-hot-toast';

export default function ResetPassword() {
  const { isDarkMode } = useTheme();
  const { resetPassword } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const inputClass = isDarkMode
    ? 'w-full px-4 py-3 rounded-xl border border-slate-600 bg-slate-800 text-white placeholder-slate-400 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/30'
    : 'w-full px-4 py-3 rounded-xl border border-slate-300 bg-white text-slate-900 placeholder-slate-500 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Invalid or missing reset link. Request a new one from the login page.');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const result = await resetPassword(token, password);
      if (result.success) {
        toast.success(result.message || 'Password updated. You can log in now.');
        navigate('/login', { replace: true });
      } else {
        toast.error(result.error || 'Reset failed');
      }
    } catch {
      toast.error('Something went wrong. Try again or request a new link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center px-4 py-12 ${
        isDarkMode ? 'bg-stone-950' : 'bg-gradient-to-br from-orange-50 via-white to-amber-50'
      }`}
    >
      <div className="w-full max-w-[21rem]">
        <div
          className={`rounded-2xl border shadow-xl overflow-hidden ${
            isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'
          }`}
        >
          <div className="px-5 py-6 text-center border-b border-slate-200/80 dark:border-slate-700">
            <Logo className="justify-center mb-3" size="md" isDarkMode={isDarkMode} />
            <h1 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Choose a new password
            </h1>
            <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              Link expires in 1 hour
            </p>
          </div>

          {!token ? (
            <div className="p-5 space-y-4 text-center">
              <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                This reset link is invalid. Use Forgot password on the login page to get a new email.
              </p>
              <Link
                to="/login"
                className="inline-block text-sm font-semibold text-orange-600 hover:text-orange-700 dark:text-orange-400"
              >
                Back to login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label
                  htmlFor="new-password"
                  className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}
                >
                  New password
                </label>
                <input
                  id="new-password"
                  type="password"
                  required
                  minLength={6}
                  autoComplete="new-password"
                  className={inputClass}
                  placeholder="At least 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <div>
                <label
                  htmlFor="confirm-password"
                  className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}
                >
                  Confirm password
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  required
                  minLength={6}
                  autoComplete="new-password"
                  className={inputClass}
                  placeholder="Repeat password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-xl font-semibold text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50"
              >
                {loading ? 'Saving…' : 'Update password'}
              </button>
              <p className="text-xs text-center">
                <Link to="/login" className="font-medium text-orange-600 dark:text-orange-400">
                  Back to login
                </Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
